import { ErrorNotification } from "@components/ErrorNotification"
import { Button } from "@components/ui/button"
import Captcha from "@components/ui/form/Captcha"
import { Field } from "@components/ui/form/Field"
import { getGraphqlClient } from "@lib/graphql-client"
import { extractErrorCode, parseError } from "@lib/parse-error"
import { useMutation } from "@tanstack/react-query"
import { Form, Formik } from "formik"
import { useRouter } from "next/router"
import React, { FC, useState } from "react"

type ResetFormValues = {
  email: string
}
type FormValues = ResetFormValues & {
  passedCaptcha: boolean
}
const { sdk } = getGraphqlClient()

export const RequestPasswordResetFormForm: FC = () => {
  const router = useRouter()
  const [error, setError] = useState<undefined | string>(undefined)
  const requestResetMutation = useMutation({
    mutationFn: async (values: ResetFormValues) => {
      const { email } = values
      await sdk.RequestResetPassword({
        input: {
          email,
          url: `${window.location.origin}/password-reset`,
        },
      })
    },
  })

  return (
    <>
      {error && <ErrorNotification message={error} />}
      <Formik
        initialValues={{
          email: "",
          passedCaptcha: false,
        }}
        validateOnChange={false}
        onSubmit={handleSubmit}
      >
        <Form className="w-full">
          <Field
            name="email"
            type="email"
            field="input"
            label="Email address"
            validation={{
              required: true,
              email: true,
            }}
          />
          <Captcha />
          <Button
            className="mb-5"
            variant="primary"
            width="fill"
            status={
              requestResetMutation.status === "pending" ? "loading" : "idle"
            }
          >
            Recover my account
          </Button>
        </Form>
      </Formik>
    </>
  )

  async function handleSubmit(formValues: FormValues) {
    try {
      const { passedCaptcha, ...values } = formValues
      if (!passedCaptcha) {
        return
      }
      await requestResetMutation.mutateAsync({
        ...values,
      })
      router.push("/password-reset/success")
    } catch (error) {
      setError(parseError(extractErrorCode(error)))
      console.error(error)
    }
  }
}
